import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiErrorDetail, API_BASE } from "../lib/api";
import { formatSaturday, formatTimeRange } from "../lib/fmt";
import Header from "../components/Header";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Skeleton } from "../components/ui/skeleton";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Calendar, Clock, MapPin, ArrowLeftRight, X, Save, CalendarPlus, Users, Truck, ClipboardList, PackageOpen, UserCircle2 } from "lucide-react";

export default function MySignup() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [edit, setEdit] = useState({ name: "", email: "", phone: "" });
  const [editing, setEditing] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [swapOpen, setSwapOpen] = useState(false);
  const [swapTarget, setSwapTarget] = useState("");
  const [working, setWorking] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/public/signups/${token}`);
      setData(data);
      setEdit({ name: data.signup.name, email: data.signup.email, phone: data.signup.phone });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Signup not found.");
      setData(false);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [token]);

  const save = async () => {
    setWorking(true);
    try {
      const { data: updated } = await api.put(`/public/signups/${token}`, edit);
      setData(updated);
      setEditing(false);
      toast.success("Signup updated.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setWorking(false); }
  };

  const cancel = async () => {
    setWorking(true);
    try {
      const { data: updated } = await api.post(`/public/signups/${token}/cancel`);
      setData(updated);
      setCancelOpen(false);
      toast.success("Slot cancelled. Thanks for letting us know!");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setWorking(false); }
  };

  const swap = async () => {
    if (!swapTarget) return toast.error("Pick a new shift first.");
    const [new_shift_id, new_role] = swapTarget.split("|");
    setWorking(true);
    try {
      const { data: updated } = await api.post(`/public/signups/${token}/swap`, {
        new_shift_id, new_role,
      });
      setData(updated);
      setSwapOpen(false);
      setSwapTarget("");
      toast.success("Swapped to the new shift!");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setWorking(false); }
  };

  if (loading) return (
    <div className="min-h-screen surface-public">
      <Header />
      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-10 space-y-4">
        <Skeleton className="h-10 w-3/4" />
        <Skeleton className="h-40 w-full rounded-xl" />
      </div>
    </div>
  );

  if (data === false) return (
    <div className="min-h-screen surface-public">
      <Header />
      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-16 text-center">
        <h1 className="font-heading text-3xl font-bold text-slate-800">Signup not found</h1>
        <p className="text-slate-500 mt-3">This link may have been cancelled or mistyped.</p>
        <Link to="/" className="inline-block mt-6">
          <Button data-testid="back-to-board-button" className="bg-brand-blue hover:bg-brand-blue-dark">Back to signup board</Button>
        </Link>
      </div>
    </div>
  );

  const { signup, shift, route, swap_options = [] } = data;
  const isCancelled = signup.status === "cancelled";

  return (
    <div className="min-h-screen surface-public">
      <Header />
      <main className="max-w-2xl mx-auto px-4 sm:px-6 py-8 sm:py-10 space-y-6">
        <div>
          <span className="inline-block text-xs uppercase tracking-[0.22em] font-semibold text-brand-blue mb-2">
            Your slot
          </span>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold text-slate-900">
            {isCancelled ? "Slot cancelled" : `Hi ${signup.name.split(" ")[0]} 👋`}
          </h1>
          <p className="text-slate-600 mt-1.5">
            {isCancelled
              ? "You're no longer signed up for this shift. You can claim a new one anytime."
              : "Here's everything about your Saturday delivery."}
          </p>
        </div>

        <div data-testid="signup-card" className="card-elevated rounded-xl p-6 space-y-4">
          <div className="flex items-start justify-between gap-3">
            <div>
              <h2 className="font-heading text-xl font-semibold text-slate-900">{route?.name}</h2>
              {signup.role_label && (
                <p className="text-sm text-brand-blue font-semibold mt-1" data-testid="signup-role-label">
                  Your role: {signup.role_label}
                </p>
              )}
              {route?.description && <p className="text-sm text-slate-500 mt-1">{route.description}</p>}
            </div>
            <span
              data-testid="signup-status-badge"
              className={`shrink-0 px-2.5 py-1 rounded-full text-[11px] font-bold uppercase tracking-wider ${
                isCancelled ? "bg-slate-200 text-slate-600" : "bg-brand-green/15 text-brand-green-dark"
              }`}
            >
              {signup.status === "confirmed" ? "Confirmed" : signup.status === "cancelled" ? "Cancelled" : signup.status}
            </span>
          </div>
          <div className="grid sm:grid-cols-2 gap-3 text-sm text-slate-700">
            <div className="flex items-center gap-2"><Calendar className="w-4 h-4 text-slate-400" />{formatSaturday(shift?.date)}</div>
            <div className="flex items-center gap-2"><Clock className="w-4 h-4 text-slate-400" />{formatTimeRange(shift?.start_time, shift?.end_time)}</div>
            <div className="flex items-center gap-2 col-span-full"><MapPin className="w-4 h-4 text-slate-400" />{shift?.pickup_location}</div>
          </div>
          {!isCancelled && (
            <a
              href={`${API_BASE}/public/signups/${token}/calendar.ics`}
              data-testid="add-to-calendar-button"
              className="mt-2 inline-flex items-center justify-center w-full min-h-[48px] rounded-lg border-2 border-brand-green/40 bg-brand-green/10 text-brand-green-dark hover:bg-brand-green/20 font-semibold transition-colors focus-ring"
            >
              <CalendarPlus className="w-5 h-5 mr-2" /> Add to my calendar
            </a>
          )}
          {!isCancelled && (
            <div data-testid="sms-toggle-row" className="mt-3 flex items-start gap-2.5 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2.5">
              <input
                type="checkbox"
                data-testid="sms-toggle-checkbox"
                checked={!!signup.sms_opt_in}
                onChange={async (e) => {
                  try {
                    const { data: updated } = await api.put(`/public/signups/${token}`, { sms_opt_in: e.target.checked });
                    setData(updated);
                    toast.success(e.target.checked ? "SMS reminders on" : "SMS reminders off");
                  } catch (err) {
                    toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not save preference.");
                  }
                }}
                className="mt-0.5 w-4 h-4 rounded border-slate-300 accent-brand-blue"
              />
              <label className="text-sm text-slate-700 leading-snug cursor-pointer" onClick={(e) => e.currentTarget.previousElementSibling.click()}>
                <span className="font-semibold text-slate-900">Text me Saturday reminders</span>
                <br />
                <span className="text-xs text-slate-500">You&apos;ll get an SMS the Wednesday before. Reply STOP any time to opt out.</span>
              </label>
            </div>
          )}
        </div>

        {!isCancelled && shift?.roles && (
          <div data-testid="team-roster-card" className="card-elevated rounded-xl p-6">
            <div className="flex items-center gap-2 mb-4">
              <Users className="w-5 h-5 text-brand-blue" />
              <h3 className="font-heading text-lg font-semibold text-slate-900">Your Saturday team</h3>
            </div>
            <ul className="space-y-2.5">
              {[
                { key: "delivery_driver", Icon: Truck },
                { key: "delivery_coordinator", Icon: ClipboardList },
                { key: "pickup_driver", Icon: PackageOpen },
              ].map(({ key, Icon }) => {
                const r = shift.roles[key];
                if (!r) return null;
                const filled = (r.signups || []).filter((s) => (s.status || "confirmed") === "confirmed");
                return (
                  <li key={key} data-testid={`team-role-${key}`} className="flex items-start gap-3 py-2 border-t border-slate-100 first:border-t-0 first:pt-0">
                    <div className="p-2 rounded-lg bg-brand-blue/10 text-brand-blue shrink-0">
                      <Icon className="w-4 h-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-slate-900 text-sm">{r.label}</div>
                      {filled.length > 0 ? (
                        <ul className="mt-1 space-y-0.5">
                          {filled.map((s) => (
                            <li key={s.id} className="flex items-center gap-1.5 text-sm text-slate-700">
                              <UserCircle2 className="w-3.5 h-3.5 text-slate-400" />
                              <span>{s.name}</span>
                              {s.id === signup.id && <span className="text-[10px] uppercase tracking-wider font-bold text-brand-green-dark bg-brand-green/15 rounded-full px-2 py-0.5 ml-1">You</span>}
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <p className="text-xs text-slate-400 italic mt-0.5">Still open — invite a teammate!</p>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>
        )}

        <div data-testid="contact-card" className="card-elevated rounded-xl p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-heading text-lg font-semibold text-slate-900">Your contact info</h3>
            {!editing && !isCancelled && (
              <Button
                data-testid="edit-button"
                variant="outline"
                size="sm"
                onClick={() => setEditing(true)}
                className="border-slate-300 hover:border-brand-blue hover:text-brand-blue"
              >
                Edit
              </Button>
            )}
          </div>
          {!editing ? (
            <dl className="grid sm:grid-cols-2 gap-3 text-sm">
              <div><dt className="text-slate-500 text-xs uppercase tracking-wider font-semibold">Name</dt><dd className="text-slate-800 mt-0.5">{signup.name}</dd></div>
              <div><dt className="text-slate-500 text-xs uppercase tracking-wider font-semibold">Email</dt><dd className="text-slate-800 mt-0.5">{signup.email}</dd></div>
              <div><dt className="text-slate-500 text-xs uppercase tracking-wider font-semibold">Phone</dt><dd className="text-slate-800 mt-0.5">{signup.phone}</dd></div>
            </dl>
          ) : (
            <div className="space-y-4">
              <div>
                <Label htmlFor="ename" className="text-sm font-semibold">Name</Label>
                <Input id="ename" data-testid="edit-name-input" value={edit.name} onChange={(e) => setEdit({ ...edit, name: e.target.value })} className="mt-1.5 text-base min-h-[44px]" />
              </div>
              <div>
                <Label htmlFor="eemail" className="text-sm font-semibold">Email</Label>
                <Input id="eemail" data-testid="edit-email-input" type="email" value={edit.email} onChange={(e) => setEdit({ ...edit, email: e.target.value })} className="mt-1.5 text-base min-h-[44px]" />
              </div>
              <div>
                <Label htmlFor="ephone" className="text-sm font-semibold">Phone</Label>
                <Input id="ephone" data-testid="edit-phone-input" type="tel" value={edit.phone} onChange={(e) => setEdit({ ...edit, phone: e.target.value })} className="mt-1.5 text-base min-h-[44px]" />
              </div>
              <div className="flex gap-2">
                <Button data-testid="save-edit-button" onClick={save} disabled={working} className="bg-brand-blue hover:bg-brand-blue-dark"><Save className="w-4 h-4 mr-1.5" /> Save changes</Button>
                <Button variant="ghost" onClick={() => { setEditing(false); setEdit({ name: signup.name, email: signup.email, phone: signup.phone }); }}>Cancel</Button>
              </div>
            </div>
          )}
        </div>

        {!isCancelled && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Button
              data-testid="swap-button"
              variant="outline"
              onClick={() => setSwapOpen(true)}
              className="min-h-[48px] border-2 border-slate-300 hover:border-brand-blue hover:text-brand-blue"
            >
              <ArrowLeftRight className="w-4 h-4 mr-2" /> Swap to a different shift
            </Button>
            <Button
              data-testid="cancel-signup-button"
              variant="outline"
              onClick={() => setCancelOpen(true)}
              className="min-h-[48px] border-2 border-rose-200 text-rose-600 hover:bg-rose-50 hover:border-rose-300"
            >
              <X className="w-4 h-4 mr-2" /> Cancel my slot
            </Button>
          </div>
        )}

        {isCancelled && (
          <Link to="/" className="block">
            <Button data-testid="browse-shifts-button" className="w-full min-h-[48px] bg-brand-blue hover:bg-brand-blue-dark">
              Browse other Saturdays
            </Button>
          </Link>
        )}
      </main>

      <Dialog open={cancelOpen} onOpenChange={setCancelOpen}>
        <DialogContent data-testid="cancel-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Cancel your slot?</DialogTitle>
            <DialogDescription>
              This will free your slot so another volunteer can claim it. You can always sign up again later.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setCancelOpen(false)}>Keep slot</Button>
            <Button data-testid="confirm-cancel-button" onClick={cancel} disabled={working} className="bg-rose-600 hover:bg-rose-700">
              Yes, cancel my slot
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={swapOpen} onOpenChange={setSwapOpen}>
        <DialogContent data-testid="swap-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Swap to a different shift</DialogTitle>
            <DialogDescription>Pick any open Saturday slot below. Your current slot will be released.</DialogDescription>
          </DialogHeader>
          <div className="my-2">
            <Label className="text-sm font-semibold">Available shifts & roles</Label>
            <Select value={swapTarget} onValueChange={setSwapTarget}>
              <SelectTrigger data-testid="swap-target-select" className="mt-1.5 min-h-[44px]">
                <SelectValue placeholder="Choose a new shift & role…" />
              </SelectTrigger>
              <SelectContent>
                {swap_options.length === 0 && <SelectItem value="__none__" disabled>No other open shifts</SelectItem>}
                {swap_options.flatMap((opt) =>
                  (opt.open_roles || []).map((role) => (
                    <SelectItem
                      key={`${opt.id}|${role.key}`}
                      value={`${opt.id}|${role.key}`}
                      data-testid={`swap-option-${opt.id}-${role.key}`}
                    >
                      {formatSaturday(opt.date)} · {opt.route?.name} · {role.label}
                    </SelectItem>
                  ))
                )}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setSwapOpen(false)}>Cancel</Button>
            <Button data-testid="confirm-swap-button" onClick={swap} disabled={working || !swapTarget} className="bg-brand-blue hover:bg-brand-blue-dark">
              Swap to this shift
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
